import { ChangeEvent, useState } from "react"
import { toast } from "react-toastify"

import { useTranslation } from "react-i18next"

import { SingleOptTimeRangePicker } from "components/date/SingleOptTimePeriodPicker"
import { DropdownMenu } from "components/inputs/DropdownMenu"
import { ModalConfirmAction } from "components/modals/ModalConfirmAction"
import { ModalDeclineAction } from "components/modals/ModalDeclineAction"
import { ModalForceDeclineAction } from "components/modals/ModalForceDeclineAction"
import { Tab, Tabs } from "components/navigation/Tabs"
import { TopBar } from "components/navigation/TopBar"
import { Table } from "components/tables/Table"

import { usePermissions } from "contexts/PermissionsContext"
import { useProps } from "contexts/PropsContext"

import { URL } from "constants/api/requests"
import { tenantOptions } from "constants/bonus"
import { PERMISSIONS } from "constants/permissions"
import { getBankTransfersColumns } from "constants/transactions"

import { Transaction } from "services/total/types"
import { transactionService } from "services/transaction/transaction.service"

import { formatFiatCurrency } from "../util/formatter"

enum TransferTab {
  SUBMITTED = "SUBMITTED",
  PENDING = "PENDING",
  CONFIRMED = "CONFIRMED",
  COMPLETED = "COMPLETED",
  DECLINED = "DECLINED"
}

const transferTabs: Tab[] = Object.keys(TransferTab).map((key) => ({ key, label: key }))

export const BankTransfers = () => {
  const { onSubmittedWithdrawalsCount, user } = useProps()
  const { has } = usePermissions()
  const canApprove = has(PERMISSIONS.WITHDRAWAL_APPROVE)
  const [activeTab, setActiveTab] = useState<TransferTab>(TransferTab.SUBMITTED)

  const { t }: { t: any } = useTranslation(["translation"], { useSuspense: false })
  const [declineRow, setDeclineRow] = useState<Transaction | null>(null)
  const [approveRow, setApproveRow] = useState<Transaction | null>(null)
  const [forceDeclineRow, setForceDeclineRow] = useState<Transaction | null>(null)
  const [tableUpdatedAt, setTableUpdatedAt] = useState<Date | number>(Date.now())
  const [timeframe, setTimeframe] = useState<(Date | null)[]>([])
  const [tenant, setTenant] = useState<string>("")
  const [failedDeclineIds, setFailedDeclineIds] = useState<Set<string>>(new Set())

  const handleChangeTimeFrame = (dates: (Date | null)[]) => setTimeframe(dates)

  const onOpenApproveRow = (row: Transaction): void => {
    setApproveRow(row)
  }

  const approveRowHandler = (): void => {
    setApproveRow(null)
    setTableUpdatedAt(Date.now())
  }

  const onApproveTransaction = async (tx: Transaction): Promise<void> => {
    if (!tx?.id) return

    const isPayAdmit = tx.provider === "PayAdmit"

    const isSubmitted = tx.status === "SUBMITTED"

    try {
      if (isPayAdmit) {
        if (isSubmitted) {
          const response = await transactionService.approveSubmittedPayadmitTransaction(tx.id, tx.status)
          if (response) toast.success(t("transactions.successAction", { actionText: "Approved" }))
        } else {
          const response = await transactionService.approvePayadmitTransaction(tx.id, tx.status)
          if (response) toast.success(t("transactions.successAction", { actionText: "Approved" }))
        }
      } else {
        if (isSubmitted) {
          const response = await transactionService.approveSubmittedCryptoTransaction(tx.id, tx.status)
          if (response) toast.success(t("transactions.successAction", { actionText: "Approved" }))
        } else {
          const response = await transactionService.approveCryptoTransaction(tx.id, tx.status)
          if (response) toast.success(t("transactions.successAction", { actionText: "Approved" }))
        }
      }
    } catch (err: any) {
      toast.error(err?.message || "Failed")
    }

    onSubmittedWithdrawalsCount()
  }

  const onOpenDeclineRow = (row: Transaction): void => {
    setDeclineRow(row)
  }

  const declineRowHandler = (): void => {
    setDeclineRow(null)
    setTableUpdatedAt(Date.now())
  }

  const onOpenForceDeclineRow = (row: Transaction): void => {
    setForceDeclineRow(row)
  }

  const forceDeclineRowHandler = (): void => {
    setForceDeclineRow(null)
    setTableUpdatedAt(Date.now())
  }

  const onForceDeclineTransaction = async (tx: Transaction, reason: string, refund: boolean): Promise<void> => {
    if (!tx?.id) return

    try {
      const response = await transactionService.forceDeclineTransaction(tx.id, reason, refund)
      if (response) {
        toast.success(`Withdrawal force declined ${refund ? "with" : "without"} refund`)
      }
    } catch (err: any) {
      toast.error(err?.message || "Failed")
    }

    onSubmittedWithdrawalsCount()
  }

  const onDeclineBankTransfer = async (tx: Transaction, reason: string): Promise<void> => {
    const isPayAdmit = tx.provider === "PayAdmit"

    const isSubmitted = tx.status === "SUBMITTED"

    try {
      if (isPayAdmit) {
        if (isSubmitted) {
          const response = await transactionService.declineSubmittedPayadmitTransaction(tx.id, reason, tx.status)
          if (response) toast.success(t("transactions.successAction", { actionText: t("common.declined") }))
        } else {
          const response = await transactionService.declinePayadmitTransaction(tx.id, reason, tx.status)
          if (response) toast.success(t("transactions.successAction", { actionText: t("common.declined") }))
        }
      } else {
        if (isSubmitted) {
          const response = await transactionService.declineSubmittedCryptoTransaction(tx.id, reason, tx.status)
          if (response) toast.success(t("transactions.successAction", { actionText: t("common.declined") }))
        } else {
          const response = await transactionService.declineCryptoTransaction(tx.id, reason, tx.status)
          if (response) toast.success(t("transactions.successAction", { actionText: t("common.declined") }))
        }
      }
    } catch (err: any) {
      toast.error(err?.message || "Decline failed")
      setFailedDeclineIds((prev) => new Set(prev).add(tx.id))
    }

    onSubmittedWithdrawalsCount()
  }

  const actions = [
    {
      name: t("common.decline"),
      condition: (row: Transaction) =>
        [TransferTab.SUBMITTED as string, TransferTab.PENDING as string].includes(row?.status) &&
        row?.type === "WITHDRAWAL" &&
        !!user &&
        canApprove,
      handler: onOpenDeclineRow,
      buttonColor: "btn-secondary"
    },
    {
      name: t("common.approve"),
      condition: (row: Transaction) =>
        [TransferTab.SUBMITTED as string, TransferTab.PENDING as string].includes(row?.status) &&
        row?.type === "WITHDRAWAL" &&
        !!user &&
        canApprove,
      handler: onOpenApproveRow
    },
    {
      name: t("common.forceDecline"),
      condition: (row: Transaction) =>
        [TransferTab.SUBMITTED as string, TransferTab.PENDING as string, TransferTab.CONFIRMED as string].includes(row?.status) &&
        row?.type === "WITHDRAWAL" &&
        !!user &&
        canApprove &&
        (row?.status === TransferTab.CONFIRMED || failedDeclineIds.has(row.id)),

      handler: onOpenForceDeclineRow,
      buttonColor: "btn-error"
    }
  ]

  const onRowClick = (row: Transaction): void => {
    if (row?.user_id) {
      const url = URL.USER.replace(":id", row.user_id)
      window.open(url, "_blank")
    }
  }

  return (
    <div className="w-full p-8 pt-0">
      <TopBar title={t("pages.withdrawals.title")} />
      <div className="w-full grid grid-col-1 gap-8 py-8">
        <div className="flex items-center justify-center">
          <Tabs tabs={transferTabs} activeTab={activeTab} onChange={(key) => setActiveTab(key as TransferTab)} variant="pills" />
        </div>

        <div className="flex flex-row gap-8 max-w-lg z-50">
          <SingleOptTimeRangePicker onTimeframeChange={handleChangeTimeFrame} noLabel />
          <DropdownMenu
            placeholder={t("form.allTenants")}
            options={tenantOptions}
            value={tenant}
            onChange={(ev: ChangeEvent<HTMLSelectElement>) => setTenant(ev.target.value)}
            rounded
          />
        </div>

        <Table
          title={t("bankTransfers.tableTitle", { tab: activeTab })}
          handler={transactionService.getTransactionsTable}
          columns={getBankTransfersColumns({
            isSubmittedTab: activeTab === TransferTab.SUBMITTED,
            canOpenViolations: has(PERMISSIONS.PAGE_BONUS2)
          })}
          actions={actions}
          updatedAt={tableUpdatedAt}
          filters={{
            type: "WITHDRAWAL",
            status: activeTab,
            createdAt: timeframe
          }}
          onRowClick={onRowClick}
          isExport={activeTab === TransferTab.COMPLETED}
          exportFileName="Withdrawals_Completed"
        />

        <ModalConfirmAction
          data={approveRow}
          handler={approveRowHandler}
          text={t("modal.confirmTransaction", {
            actionText: "approve",
            usd_amount: formatFiatCurrency(approveRow?.usd_amount ?? undefined),
            user: approveRow?.user
          })}
          actionText={t("common.approve")}
          actionHandler={onApproveTransaction}
        />
        <ModalDeclineAction
          data={declineRow}
          handler={declineRowHandler}
          text={t("modal.confirmTransaction", {
            actionText: "decline",
            usd_amount: formatFiatCurrency(declineRow?.usd_amount ?? undefined),
            user: declineRow?.user
          })}
          actionText={t("common.decline")}
          actionHandler={(data: string, reason: string) => onDeclineBankTransfer(data as unknown as Transaction, reason)}
          isFullData={true}
        />
        <ModalForceDeclineAction data={forceDeclineRow} handler={forceDeclineRowHandler} actionHandler={onForceDeclineTransaction} />
      </div>
    </div>
  )
}
